import type { TacticalBoard, TacticalFeature } from "@/services/tactical.service";

export const TACTICAL_BOARD_FORMAT = "geopulseml.tactical.board" as const;
export const TACTICAL_BOARD_VERSION = 1;

export type TacticalBoardPackage = {
  format: typeof TACTICAL_BOARD_FORMAT;
  version: number;
  exported_at: string;
  board?: {
    id?: number;
    name?: string;
    kind?: string;
  };
  featureCollection: {
    type: "FeatureCollection";
    features: TacticalFeature[];
  };
};

export type FeatureCreatePayload = Record<string, unknown>;

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

function asHex(color: string | undefined, fallback: string): string {
  if (color && HEX6.test(color)) return color;
  return fallback;
}

/** Собрать пакет для скачивания. */
export function buildBoardPackage(
  board: Pick<TacticalBoard, "id" | "name" | "kind"> | null,
  features: TacticalFeature[],
): TacticalBoardPackage {
  return {
    format: TACTICAL_BOARD_FORMAT,
    version: TACTICAL_BOARD_VERSION,
    exported_at: new Date().toISOString(),
    board: board
      ? { id: board.id, name: board.name, kind: board.kind }
      : undefined,
    featureCollection: {
      type: "FeatureCollection",
      features: features.map((f) => ({
        type: "Feature",
        geometry: f.geometry,
        properties: { ...f.properties },
      })),
    },
  };
}

function isFeatureCollection(v: unknown): v is GeoJSON.FeatureCollection {
  return (
    !!v &&
    typeof v === "object" &&
    (v as { type?: string }).type === "FeatureCollection" &&
    Array.isArray((v as { features?: unknown }).features)
  );
}

/** Разобрать пакет или сырой FeatureCollection → список Feature. */
export function parseBoardImport(raw: unknown): {
  features: GeoJSON.Feature[];
  boardName?: string;
} {
  if (!raw || typeof raw !== "object") {
    throw new Error("Ожидался JSON-объект");
  }
  const obj = raw as Record<string, unknown>;
  if (obj.format === TACTICAL_BOARD_FORMAT && isFeatureCollection(obj.featureCollection)) {
    const board = obj.board as { name?: string } | undefined;
    return {
      features: obj.featureCollection.features.filter(
        (f): f is GeoJSON.Feature => !!f && f.type === "Feature" && !!f.geometry,
      ),
      boardName: board?.name,
    };
  }
  if (isFeatureCollection(obj)) {
    return {
      features: obj.features.filter(
        (f): f is GeoJSON.Feature => !!f && f.type === "Feature" && !!f.geometry,
      ),
    };
  }
  throw new Error("Нужен пакет geopulseml.tactical.board или FeatureCollection");
}

function ringWithoutClose(ring: number[][]): [number, number][] {
  if (ring.length < 2) return ring.map((c) => [c[0]!, c[1]!]);
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  const closed =
    first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
  return closed.map((c) => [Number(c[0]), Number(c[1])] as [number, number]);
}

/**
 * GeoJSON Feature → payload POST /features.
 * Пропускает неподдерживаемую геометрию (null).
 */
export function featureToCreatePayload(
  feature: GeoJSON.Feature,
): FeatureCreatePayload | null {
  const geom = feature.geometry;
  if (!geom) return null;
  const props = (feature.properties ?? {}) as Record<string, unknown>;
  const kindProp = props.kind;
  let kind: "point" | "line" | "polygon" | "text" =
    kindProp === "text" || kindProp === "point" || kindProp === "line" || kindProp === "polygon"
      ? kindProp
      : geom.type === "Point"
        ? "point"
        : geom.type === "LineString"
          ? "line"
          : geom.type === "Polygon"
            ? "polygon"
            : "point";

  if (kind === "text" && geom.type !== "Point") kind = "point";
  if (kind === "point" && geom.type !== "Point") return null;
  if (kind === "line" && geom.type !== "LineString") return null;
  if (kind === "polygon" && geom.type !== "Polygon") return null;

  const stroke = asHex(
    typeof props.strokeColor === "string" ? props.strokeColor : undefined,
    "#dc2626",
  );
  const fill = asHex(
    typeof props.fillColor === "string" ? props.fillColor : undefined,
    stroke,
  );
  const label =
    typeof props.label === "string"
      ? props.label
      : typeof props.name === "string"
        ? props.name
        : undefined;
  const sizePx =
    typeof props.sizePx === "number" && props.sizePx > 0 ? props.sizePx : undefined;
  const rotationDeg =
    typeof props.rotationDeg === "number" ? props.rotationDeg : undefined;
  const strokeWidth =
    typeof props.strokeWidth === "number" && props.strokeWidth > 0
      ? props.strokeWidth
      : undefined;
  const fillOpacity =
    typeof props.fillOpacity === "number" ? props.fillOpacity : undefined;
  const symbolKlsId =
    typeof props.symbolKlsId === "number" ? props.symbolKlsId : undefined;
  const originId = props.id ?? props.originFeatureId;

  const base: FeatureCreatePayload = {
    kind,
    stroke_color: stroke,
    fill_color: fill,
    ...(label != null && label !== "" ? { label: String(label).slice(0, 200) } : {}),
    ...(sizePx != null ? { size_px: sizePx } : {}),
    ...(rotationDeg != null ? { rotation_deg: rotationDeg } : {}),
    ...(strokeWidth != null ? { stroke_width: strokeWidth } : {}),
    ...(fillOpacity != null ? { fill_opacity: fillOpacity } : {}),
    ...(symbolKlsId != null && kind === "point" ? { symbol_kls_id: symbolKlsId } : {}),
    ...(originId != null
      ? { properties: { originFeatureId: String(originId) } }
      : {}),
  };

  if (kind === "point" || kind === "text") {
    const c = (geom as GeoJSON.Point).coordinates;
    if (kind === "text" && !(label && String(label).trim())) {
      return null;
    }
    return { ...base, lng: c[0], lat: c[1] };
  }
  if (kind === "line") {
    const coords = (geom as GeoJSON.LineString).coordinates.map(
      (c) => [Number(c[0]), Number(c[1])] as [number, number],
    );
    if (coords.length < 2) return null;
    return { ...base, coordinates: coords };
  }
  const outer = (geom as GeoJSON.Polygon).coordinates[0];
  if (!outer) return null;
  const coords = ringWithoutClose(outer);
  if (coords.length < 3) return null;
  return { ...base, coordinates: coords };
}

export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function readJsonFile(file: File): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        resolve(JSON.parse(String(reader.result)));
      } catch {
        reject(new Error("Файл не является валидным JSON"));
      }
    };
    reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
    reader.readAsText(file);
  });
}
